import React, { useState, useContext, useRef } from 'react';
import { Printer, FileText, Plus, Minus, AlignLeft, Check, Download } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import { AuthContext } from '../App';

// ─── SVG ICON STRINGS (for print window injection) ─────────────────────
const ICO = {
    pin: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>`,
    phone: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
    mail: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
    globe: `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`,
};

// ─── DESIGN TEMPLATES ──────────────────────────────────────────────────
const getDesignTemplates = (companyName, logoUrl, companyData) => {
    const slug = companyName.toLowerCase().replace(/\s+/g, '');

    const footerBlock = (iconColor, labelColor, textColor, bgColor, borderColor) => {
        const address = companyData?.address || 'Shop No. 04, Near Landmark,<br/>Virar (W), Palghar - 401303';
        const contact = companyData?.contact_no || '+91 98XXX XXXXX';
        const email = companyData?.email || `info@${slug}.com`;
        const website = companyData?.website || `www.${slug}.com`;

        return `
        <div style="display:flex;align-items:flex-start;gap:8px;">
            <div style="margin-top:1px;width:24px;height:24px;background:${bgColor};border-radius:7px;display:flex;align-items:center;justify-content:center;border:1px solid ${borderColor};color:${iconColor};">${ICO.pin}</div>
            <div><p style="font-size:7px;font-weight:800;color:${labelColor};text-transform:uppercase;letter-spacing:2px;margin:0;">Address</p><p style="font-size:9px;font-weight:600;color:${textColor};margin:2px 0 0;line-height:1.4;">${address}</p></div>
        </div>
        <div style="display:flex;align-items:flex-start;gap:8px;">
            <div style="margin-top:1px;width:24px;height:24px;background:${bgColor};border-radius:7px;display:flex;align-items:center;justify-content:center;border:1px solid ${borderColor};color:${iconColor};">${ICO.phone}</div>
            <div><p style="font-size:7px;font-weight:800;color:${labelColor};text-transform:uppercase;letter-spacing:2px;margin:0;">Contact</p><p style="font-size:9px;font-weight:600;color:${textColor};margin:2px 0 0;">${contact}</p></div>
        </div>
        <div style="display:flex;align-items:flex-start;gap:8px;">
            <div style="margin-top:1px;width:24px;height:24px;background:${bgColor};border-radius:7px;display:flex;align-items:center;justify-content:center;border:1px solid ${borderColor};color:${iconColor};">${ICO.mail}</div>
            <div><p style="font-size:7px;font-weight:800;color:${labelColor};text-transform:uppercase;letter-spacing:2px;margin:0;">Email</p><p style="font-size:9px;font-weight:600;color:${textColor};margin:2px 0 0;">${email}</p></div>
        </div>
        <div style="display:flex;align-items:flex-start;gap:8px;">
            <div style="margin-top:1px;width:24px;height:24px;background:${bgColor};border-radius:7px;display:flex;align-items:center;justify-content:center;border:1px solid ${borderColor};color:${iconColor};">${ICO.globe}</div>
            <div><p style="font-size:7px;font-weight:800;color:${labelColor};text-transform:uppercase;letter-spacing:2px;margin:0;">Website</p><p style="font-size:9px;font-weight:600;color:${textColor};margin:2px 0 0;">${website}</p></div>
        </div>
        `;
    };

    const gstin = companyData?.gstin_no || '27AAAAA0000A1Z5';


    const crossWatermark = (color, op1 = 0.025, op2 = 0.018) => `
        <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);opacity:${op1};pointer-events:none;white-space:nowrap;">
            <div style="font-size:90px;font-weight:900;color:${color};text-transform:uppercase;letter-spacing:12px;line-height:1.5;text-align:center;">${companyName}<br/>${companyName}<br/>${companyName}</div>
        </div>
        <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(35deg);opacity:${op2};pointer-events:none;white-space:nowrap;">
            <div style="font-size:70px;font-weight:900;color:${color};text-transform:uppercase;letter-spacing:15px;line-height:1.8;text-align:center;">${companyName}<br/>${companyName}<br/>${companyName}</div>
        </div>
    `;

    return [
        // ── DESIGN 1: MODERN INDUSTRIAL ──
        {
            id: 'corporate',
            name: 'Modern Industrial',
            desc: 'Clean concrete & steel tones with indigo accents',
            accent: '#4f46e5',
            preview: { headerBg: '#0f172a', accent: '#4f46e5' },
            render: (pageNum) => `
                <div class="letterhead-page">
                    <div style="height:5px;background:linear-gradient(90deg,#0f172a,#1e293b,#334155,#1e293b,#0f172a);"></div>
                    <div style="padding:28px 48px 20px;display:flex;align-items:center;justify-content:space-between;border-bottom:2.5px solid #0f172a;">
                        <div style="display:flex;align-items:center;gap:16px;">
                            <img src="${logoUrl}" alt="Logo" style="height:72px;width:auto;max-width:220px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:28px;font-weight:900;color:#0f172a;margin:0;letter-spacing:-0.5px;text-transform:uppercase;line-height:1;">${companyName}</h1>
                                <p style="font-size:9px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:4px;margin:5px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                        </div>
                        <div style="text-align:right;border-left:2px solid #e2e8f0;padding-left:20px;">
                            <p style="font-size:8px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:3px;margin:0 0 4px;">GSTIN</p>
                            <p style="font-size:14px;font-weight:900;color:#0f172a;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    <div style="height:1px;background:linear-gradient(90deg,transparent,#e2e8f0,transparent);"></div>
                    <div style="min-height:calc(297mm - 230px);position:relative;overflow:hidden;padding:48px;">
                        ${crossWatermark('#0f172a')}
                    </div>
                    <div style="position:absolute;bottom:0;left:0;right:0;">
                        <div style="margin:0 48px;height:1px;background:linear-gradient(90deg,transparent,#cbd5e1,transparent);"></div>
                        <div style="padding:18px 48px 14px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#4f46e5', '#94a3b8', '#475569', '#f1f5f9', '#e2e8f0')}
                        </div>
                        <div style="height:5px;background:linear-gradient(90deg,#0f172a,#1e293b,#334155,#1e293b,#0f172a);"></div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 2: CLASSIC TIMBER & GOLD ──
        {
            id: 'royal',
            name: 'Classic Timber & Gold',
            desc: 'Luxurious oak & gold tones with ornamental borders',
            accent: '#b45309',
            preview: { headerBg: '#78350f', accent: '#f59e0b' },
            render: (pageNum) => `
                <div class="letterhead-page">
                    <div style="height:6px;background:linear-gradient(90deg,#78350f,#b45309,#f59e0b,#b45309,#78350f);"></div>
                    <div style="padding:24px 48px 18px;display:flex;align-items:center;justify-content:space-between;border-bottom:3px double #b45309;">
                        <div style="display:flex;align-items:center;gap:16px;">
                            <img src="${logoUrl}" alt="Logo" style="height:72px;width:auto;max-width:220px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:26px;font-weight:900;color:#78350f;margin:0;text-transform:uppercase;letter-spacing:3px;line-height:1;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:5px;margin:6px 0 0 3px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                        </div>
                        <div style="text-align:right;background:#fffbeb;padding:12px 20px;border-radius:12px;border:1px solid #fde68a;">
                            <p style="font-size:7px;font-weight:800;color:#92400e;text-transform:uppercase;letter-spacing:3px;margin:0 0 3px;">GSTIN</p>
                            <p style="font-size:13px;font-weight:900;color:#78350f;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    <div style="min-height:calc(297mm - 230px);position:relative;overflow:hidden;padding:48px;">
                        ${crossWatermark('#78350f', 0.03, 0.02)}
                    </div>
                    <div style="position:absolute;bottom:0;left:0;right:0;">
                        <div style="margin:0 48px;height:1px;background:linear-gradient(90deg,transparent,#f59e0b,transparent);"></div>
                        <div style="padding:18px 48px 14px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#b45309', '#92400e', '#78350f', '#fffbeb', '#fde68a')}
                        </div>
                        <div style="height:6px;background:linear-gradient(90deg,#78350f,#b45309,#f59e0b,#b45309,#78350f);"></div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 4: STUDIO VERTICAL ──
        {
            id: 'sidebar',
            name: 'Studio Vertical',
            desc: 'Striking left sidebar with vertical branding',
            accent: '#7c3aed',
            preview: { headerBg: '#7c3aed', accent: '#7c3aed' },
            render: (pageNum) => `
                <div class="letterhead-page" style="display:flex !important;min-height:297mm;">
                    <!-- Left Sidebar -->
                    <div style="width:60px;background:linear-gradient(180deg,#5b21b6,#7c3aed,#8b5cf6);display:flex;flex-direction:column;align-items:center;padding:24px 0;position:relative;">
                        <img src="${logoUrl}" alt="Logo" style="width:48px;height:auto;max-height:80px;object-fit:contain;"/>
                        <div style="writing-mode:vertical-rl;text-orientation:mixed;transform:rotate(180deg);color:rgba(255,255,255,0.4);font-size:9px;font-weight:900;letter-spacing:6px;text-transform:uppercase;margin-top:auto;margin-bottom:auto;">
                            ${companyName}
                        </div>
                    </div>
                    <!-- Main Content -->
                    <div style="flex:1;position:relative;">
                        <div style="padding:28px 44px 20px;display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #7c3aed;">
                            <div>
                                <h1 style="font-size:28px;font-weight:900;color:#1e1b4b;margin:0;text-transform:uppercase;letter-spacing:1px;line-height:1;">${companyName}</h1>
                                <p style="font-size:9px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:4px;margin:5px 0 0 1px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                            <div style="text-align:right;">
                                <div style="background:#7c3aed;color:white;padding:6px 14px;border-radius:8px;font-size:8px;font-weight:900;letter-spacing:2px;text-transform:uppercase;display:inline-block;margin-bottom:4px;">GSTIN</div>
                                <p style="font-size:13px;font-weight:900;color:#1e1b4b;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                            </div>
                        </div>
                        <div style="min-height:calc(297mm - 230px);position:relative;overflow:hidden;padding:44px;">
                            ${crossWatermark('#5b21b6', 0.02, 0.015)}
                        </div>
                        <div style="position:absolute;bottom:0;left:0;right:0;">
                            <div style="margin:0 44px;height:1px;background:linear-gradient(90deg,transparent,#c4b5fd,transparent);"></div>
                            <div style="padding:18px 44px 14px;display:flex;justify-content:space-between;align-items:center;">
                                ${footerBlock('#7c3aed', '#7c3aed', '#4c1d95', '#f5f3ff', '#ede9fe')}
                            </div>
                            <div style="height:4px;background:linear-gradient(90deg,#5b21b6,#7c3aed,#8b5cf6);"></div>
                        </div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 5: BOTANICAL VELVET ──
        {
            id: 'emerald',
            name: 'Botanical Velvet',
            desc: 'Refined botanical green tones with a sophisticated frame',
            accent: '#059669',
            preview: { headerBg: '#064e3b', accent: '#10b981' },
            render: (pageNum) => `
                <div class="letterhead-page">
                    <div style="height:4px;background:linear-gradient(90deg,#064e3b,#059669,#34d399,#059669,#064e3b);"></div>
                    <div style="margin:12px;border:1.5px solid #d1fae5;border-radius:4px;min-height:calc(297mm - 32px);position:relative;overflow:hidden;">
                        <div style="padding:24px 40px 18px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #a7f3d0;">
                            <div style="display:flex;align-items:center;gap:16px;">
                                <img src="${logoUrl}" alt="Logo" style="height:72px;width:auto;max-width:220px;object-fit:contain;"/>
                                <div>
                                    <h1 style="font-size:26px;font-weight:900;color:#064e3b;margin:0;text-transform:uppercase;letter-spacing:1px;line-height:1;">${companyName}</h1>
                                    <p style="font-size:9px;font-weight:700;color:#059669;text-transform:uppercase;letter-spacing:4px;margin:5px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                                </div>
                            </div>
                            <div style="text-align:right;background:#ecfdf5;padding:10px 18px;border-radius:10px;border:1px solid #a7f3d0;">
                                <p style="font-size:7px;font-weight:800;color:#059669;text-transform:uppercase;letter-spacing:3px;margin:0 0 3px;">GSTIN</p>
                                <p style="font-size:13px;font-weight:900;color:#064e3b;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                            </div>
                        </div>
                        <div style="min-height:calc(297mm - 280px);position:relative;overflow:hidden;padding:44px 40px;">
                            ${crossWatermark('#064e3b', 0.025, 0.018)}
                        </div>
                        <div style="position:absolute;bottom:0;left:0;right:0;">
                            <div style="margin:0 40px;height:1px;background:linear-gradient(90deg,transparent,#6ee7b7,transparent);"></div>
                            <div style="padding:16px 40px 12px;display:flex;justify-content:space-between;align-items:center;">
                                ${footerBlock('#059669', '#059669', '#064e3b', '#ecfdf5', '#a7f3d0')}
                            </div>
                        </div>
                    </div>
                    <div style="height:4px;background:linear-gradient(90deg,#064e3b,#059669,#34d399,#059669,#064e3b);position:absolute;bottom:0;left:0;right:0;"></div>
                </div>
            `
        },

        // ── DESIGN 6: CONTEMPORARY MATTE ──
        {
            id: 'dark',
            name: 'Contemporary Matte',
            desc: 'Premium dark matte header band with rose-red contrast',
            accent: '#e11d48',
            preview: { headerBg: '#0f172a', accent: '#e11d48' },
            render: (pageNum) => `
                <div class="letterhead-page">
                    <div style="background:#0f172a;padding:28px 48px 24px;display:flex;align-items:center;justify-content:space-between;">
                        <div style="display:flex;align-items:center;gap:16px;">
                            <img src="${logoUrl}" alt="Logo" style="height:72px;width:auto;max-width:220px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:26px;font-weight:900;color:#ffffff;margin:0;text-transform:uppercase;letter-spacing:2px;line-height:1;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:5px;margin:6px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#475569;text-transform:uppercase;letter-spacing:3px;margin:0 0 4px;">GSTIN</p>
                            <p style="font-size:14px;font-weight:900;color:#f8fafc;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    <div style="height:3px;background:linear-gradient(90deg,#e11d48,#f43f5e,#fb7185,#f43f5e,#e11d48);"></div>
                    <div style="min-height:calc(297mm - 230px);position:relative;overflow:hidden;padding:48px;">
                        ${crossWatermark('#0f172a', 0.025, 0.018)}
                    </div>
                    <div style="position:absolute;bottom:0;left:0;right:0;">
                        <div style="height:3px;background:linear-gradient(90deg,#e11d48,#f43f5e,#fb7185,#f43f5e,#e11d48);"></div>
                        <div style="background:#0f172a;padding:18px 48px 14px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#f43f5e', '#64748b', '#e2e8f0', 'rgba(255,255,255,0.05)', 'rgba(255,255,255,0.1)')}
                        </div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 7: ABSTRACT GEOMETRY ──
        {
            id: 'abstract_geo',
            name: 'Art Deco Geometry',
            desc: 'Creative abstract shapes and elegant gold & navy accents',
            accent: '#0369a1',
            preview: { headerBg: '#ffffff', accent: '#0369a1' },
            render: (pageNum) => `
                <div class="letterhead-page" style="background:#fafafa;position:relative;min-height:297mm;">
                    <!-- Abstract Background Shapes -->
                    <div style="position:absolute;top:-50px;right:-50px;width:300px;height:300px;border-radius:50%;background:linear-gradient(135deg,rgba(14,165,233,0.1),rgba(3,105,161,0.05));pointer-events:none;"></div>
                    <div style="position:absolute;bottom:-100px;left:-50px;width:400px;height:400px;border-radius:50%;background:linear-gradient(135deg,rgba(245,158,11,0.1),rgba(217,119,6,0.05));pointer-events:none;"></div>
                    
                    <div style="padding:40px 48px 24px;display:flex;align-items:center;justify-content:space-between;position:relative;z-index:2;">
                        <div style="display:flex;align-items:center;gap:16px;">
                            <img src="${logoUrl}" alt="Logo" style="height:72px;width:auto;max-width:220px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:26px;font-weight:900;color:#0369a1;margin:0;letter-spacing:1px;line-height:1;font-family:Georgia,serif;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:700;color:#d97706;text-transform:uppercase;letter-spacing:4px;margin:6px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:3px;margin:0 0 4px;">GSTIN</p>
                            <p style="font-size:13px;font-weight:900;color:#0f172a;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    
                    <div style="margin:0 48px;height:2px;background:linear-gradient(90deg,#0369a1,transparent);"></div>
                    
                    <div style="min-height:calc(297mm - 230px);position:relative;overflow:hidden;padding:48px;z-index:2;">
                        ${crossWatermark('#0369a1', 0.015, 0.01)}
                    </div>
                    
                    <div style="position:absolute;bottom:0;left:0;right:0;z-index:2;background:rgba(255,255,255,0.8);backdrop-filter:blur(10px);">
                        <div style="margin:0 48px;height:1px;background:#e2e8f0;"></div>
                        <div style="padding:20px 48px 16px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#0369a1', '#94a3b8', '#334155', '#f0f9ff', '#bae6fd')}
                        </div>
                        <div style="height:8px;background:linear-gradient(90deg,#0369a1,#0ea5e9,#f59e0b);"></div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 8: ARCHITECTURAL MONOGRAM ──
        {
            id: 'monogram_arch',
            name: 'Blueprint Monogram',
            desc: 'Technical architectural aesthetics with a striking monogram',
            accent: '#334155',
            preview: { headerBg: '#f8fafc', accent: '#334155' },
            render: (pageNum) => `
                <div class="letterhead-page" style="border:10px solid #f8fafc;position:relative;min-height:297mm;">
                    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:600px;font-weight:900;color:rgba(51,65,85,0.03);line-height:1;font-family:serif;pointer-events:none;">
                        ${companyName.charAt(0).toUpperCase()}
                    </div>
                    
                    <div style="position:absolute;inset:20px;border:1px solid #e2e8f0;pointer-events:none;"></div>
                    <div style="position:absolute;inset:24px;border:1px solid rgba(226,232,240,0.5);pointer-events:none;"></div>
                    
                    <div style="padding:44px 44px 20px;display:flex;align-items:center;justify-content:space-between;position:relative;">
                        <div style="display:flex;align-items:center;gap:20px;">
                            <img src="${logoUrl}" alt="Logo" style="height:64px;width:auto;max-width:200px;object-fit:contain;"/>
                            <div style="border-left:2px solid #334155;padding-left:20px;">
                                <h1 style="font-size:22px;font-weight:900;color:#0f172a;margin:0;letter-spacing:4px;text-transform:uppercase;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:6px;margin:6px 0 0 0;">ARCHITECTURAL & INTERIOR DESIGN</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <div style="border:1px solid #cbd5e1;padding:6px 12px;display:inline-block;">
                                <p style="font-size:6px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:4px;margin:0 0 2px;">GSTIN / REG</p>
                                <p style="font-size:11px;font-weight:900;color:#334155;letter-spacing:3px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div style="min-height:calc(297mm - 220px);position:relative;overflow:hidden;padding:44px;"></div>
                    
                    <div style="position:absolute;bottom:0;left:0;right:0;padding:24px 44px;">
                        <div style="border-top:1px solid #e2e8f0;padding-top:20px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#334155', '#94a3b8', '#475569', '#f8fafc', '#e2e8f0')}
                        </div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 9: FLUID WAVES ──
        {
            id: 'fluid_wave',
            name: 'Organic Waves',
            desc: 'Modern fluid aesthetics with sweeping organic curves',
            accent: '#be123c',
            preview: { headerBg: '#fff1f2', accent: '#f43f5e' },
            render: (pageNum) => `
                <div class="letterhead-page" style="background:#ffffff;position:relative;min-height:297mm;">
                    <!-- Top Wave SVG -->
                    <svg viewBox="0 0 800 200" style="position:absolute;top:0;left:0;width:100%;height:auto;z-index:0;" preserveAspectRatio="none">
                        <path d="M0,0 L800,0 L800,50 Q600,200 400,100 Q200,0 0,150 Z" fill="#fff1f2" />
                        <path d="M0,0 L800,0 L800,30 Q600,160 400,80 Q200,0 0,100 Z" fill="#ffe4e6" />
                        <path d="M0,0 L800,0 L800,10 Q600,120 400,60 Q200,0 0,60 Z" fill="#fda4af" opacity="0.3" />
                    </svg>
                    
                    <div style="padding:40px 48px 24px;display:flex;align-items:center;justify-content:space-between;position:relative;z-index:2;">
                        <div style="display:flex;align-items:center;gap:16px;">
                            <img src="${logoUrl}" alt="Logo" style="height:80px;width:auto;max-width:220px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:28px;font-weight:900;color:#881337;margin:0;letter-spacing:-0.5px;line-height:1;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:800;color:#be123c;text-transform:uppercase;letter-spacing:5px;margin:8px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#f43f5e;text-transform:uppercase;letter-spacing:3px;margin:0 0 4px;">GSTIN</p>
                            <p style="font-size:14px;font-weight:900;color:#881337;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    
                    <div style="min-height:calc(297mm - 260px);position:relative;overflow:hidden;padding:48px;z-index:2;">
                        ${crossWatermark('#881337', 0.015, 0.01)}
                    </div>
                    
                    <!-- Bottom Wave SVG -->
                    <svg viewBox="0 0 800 150" style="position:absolute;bottom:0;left:0;width:100%;height:auto;z-index:0;" preserveAspectRatio="none">
                        <path d="M0,150 L800,150 L800,50 Q600,-50 400,50 Q200,150 0,50 Z" fill="#fff1f2" />
                    </svg>
                    
                    <div style="position:absolute;bottom:0;left:0;right:0;z-index:2;">
                        <div style="padding:20px 48px 24px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#e11d48', '#881337', '#4c0519', '#ffe4e6', '#fda4af')}
                        </div>
                    </div>
                </div>
            `
        },

        // ── DESIGN 10: TYPOGRAPHIC GRID ──
        {
            id: 'typo_grid',
            name: 'Swiss Typographic',
            desc: 'Ultra-modern grid layout with massive background letterforms',
            accent: '#000000',
            preview: { headerBg: '#f3f4f6', accent: '#000000' },
            render: (pageNum) => {
                const chars = companyName.split('').filter(c => c.trim()).slice(0, 4);
                return `
                <div class="letterhead-page" style="background:#ffffff;border:1px solid #e5e7eb;position:relative;min-height:297mm;">
                    <!-- Massive Typographic Backgrounds -->
                    <div style="position:absolute;top:5%;left:-5%;font-size:400px;font-weight:900;color:#f3f4f6;line-height:0.8;font-family:sans-serif;pointer-events:none;transform:rotate(-15deg);opacity:0.6;">${chars[0] || 'I'}</div>
                    <div style="position:absolute;top:40%;right:-10%;font-size:500px;font-weight:900;color:#f3f4f6;line-height:0.8;font-family:sans-serif;pointer-events:none;transform:rotate(10deg);opacity:0.4;">${chars[1] || 'N'}</div>
                    <div style="position:absolute;bottom:-5%;left:20%;font-size:300px;font-weight:900;color:#f3f4f6;line-height:0.8;font-family:sans-serif;pointer-events:none;transform:rotate(-5deg);opacity:0.5;">${chars[2] || 'T'}</div>
                    
                    <!-- Strict Grid Structure -->
                    <div style="position:absolute;left:24px;top:0;bottom:0;width:1px;background:#f3f4f6;"></div>
                    <div style="position:absolute;left:48px;top:0;bottom:0;width:1px;background:#e5e7eb;"></div>
                    <div style="position:absolute;right:48px;top:0;bottom:0;width:1px;background:#e5e7eb;"></div>
                    <div style="position:absolute;top:140px;left:0;right:0;height:1px;background:#e5e7eb;"></div>
                    <div style="position:absolute;bottom:80px;left:0;right:0;height:1px;background:#e5e7eb;"></div>
                    
                    <div style="padding:40px 48px;display:flex;align-items:flex-start;justify-content:space-between;position:relative;z-index:2;">
                        <div style="display:flex;align-items:flex-start;gap:24px;">
                            <img src="${logoUrl}" alt="Logo" style="height:60px;width:auto;max-width:180px;object-fit:contain;"/>
                            <div style="padding-top:4px;">
                                <h1 style="font-size:24px;font-weight:900;color:#000000;margin:0;letter-spacing:-1px;line-height:1;text-transform:lowercase;">${companyName}</h1>
                                <p style="font-size:9px;font-weight:700;color:#6b7280;letter-spacing:1px;margin:6px 0 0 2px;">INTERIOR DESIGN ARCHITECTURE</p>
                            </div>
                        </div>
                        <div style="text-align:right;padding-top:4px;">
                            <p style="font-size:7px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:2px;margin:0 0 4px;">GSTIN IDENTIFIER</p>
                            <p style="font-size:12px;font-weight:700;color:#000000;letter-spacing:1px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    
                    <div style="min-height:calc(297mm - 220px);position:relative;overflow:hidden;padding:48px;z-index:2;"></div>
                    
                    <div style="position:absolute;bottom:0;left:0;right:0;z-index:2;height:80px;display:flex;align-items:center;padding:0 48px;">
                        <div style="width:100%;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#000000', '#6b7280', '#000000', '#f3f4f6', '#e5e7eb')}
                        </div>
                    </div>
                </div>
                `;
            }
        },

        // ── DESIGN 11: POLYGON CORE ──
        {
            id: 'polygon_core',
            name: 'Polygon Core',
            desc: 'Striking structural polygon watermark with warm terracotta edges',
            accent: '#c2410c',
            preview: { headerBg: '#ffffff', accent: '#c2410c' },
            render: (pageNum) => `
                <div class="letterhead-page" style="background:#ffffff;position:relative;min-height:297mm;">
                    <!-- Polygon SVG Watermark -->
                    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:0;opacity:0.04;pointer-events:none;">
                        <svg width="600" height="600" viewBox="0 0 100 100">
                            <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" fill="none" stroke="#c2410c" stroke-width="2"/>
                            <polygon points="50,15 85,32 85,68 50,85 15,68 15,32" fill="none" stroke="#c2410c" stroke-width="1"/>
                            <line x1="5" y1="25" x2="95" y2="75" stroke="#c2410c" stroke-width="1"/>
                            <line x1="5" y1="75" x2="95" y2="25" stroke="#c2410c" stroke-width="1"/>
                            <line x1="50" y1="5" x2="50" y2="95" stroke="#c2410c" stroke-width="1"/>
                        </svg>
                    </div>
                    
                    <!-- Top Right Corner -->
                    <div style="position:absolute;top:0;right:0;width:150px;height:150px;background:linear-gradient(225deg,#fed7aa 0%,transparent 50%);"></div>
                    <div style="position:absolute;top:0;right:0;width:60px;height:6px;background:#c2410c;"></div>
                    <div style="position:absolute;top:0;right:0;width:6px;height:60px;background:#c2410c;"></div>
                    
                    <!-- Bottom Left Corner -->
                    <div style="position:absolute;bottom:0;left:0;width:150px;height:150px;background:linear-gradient(45deg,#fed7aa 0%,transparent 50%);"></div>
                    <div style="position:absolute;bottom:0;left:0;width:60px;height:6px;background:#c2410c;"></div>
                    <div style="position:absolute;bottom:0;left:0;width:6px;height:60px;background:#c2410c;"></div>
                    
                    <div style="padding:48px 56px 20px;display:flex;align-items:center;justify-content:space-between;position:relative;z-index:2;border-bottom:1px solid #ffedd5;">
                        <div style="display:flex;align-items:center;gap:20px;">
                            <img src="${logoUrl}" alt="Logo" style="height:70px;width:auto;max-width:200px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:26px;font-weight:900;color:#431407;margin:0;letter-spacing:1px;line-height:1;">${companyName}</h1>
                                <p style="font-size:8px;font-weight:700;color:#c2410c;text-transform:uppercase;letter-spacing:5px;margin:6px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#ea580c;text-transform:uppercase;letter-spacing:3px;margin:0 0 4px;">GSTIN</p>
                            <p style="font-size:13px;font-weight:900;color:#7c2d12;letter-spacing:2px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                    </div>
                    
                    <div style="min-height:calc(297mm - 250px);position:relative;overflow:hidden;padding:48px 56px;z-index:2;"></div>
                    
                    <div style="position:absolute;bottom:0;left:0;right:0;z-index:2;">
                        <div style="padding:24px 56px;display:flex;justify-content:space-between;align-items:center;">
                            ${footerBlock('#c2410c', '#ea580c', '#7c2d12', '#fff7ed', '#ffedd5')}
                        </div>
                    </div>
                </div>
            `
        }
    ];
};


// ─── MAIN COMPONENT ────────────────────────────────────────────────────
const Letterhead = () => {
    const { user, company } = useContext(AuthContext);
    const [copies, setCopies] = useState(1);
    const [selectedDesign, setSelectedDesign] = useState('corporate');

    const companyName = (user?.company_name || 'Your Company Name').toUpperCase();
    const slug = companyName.toLowerCase().replace(/\s+/g, '');
    const logoUrl = user?.logo
        ? `https://backendibos.laxmaninterior.in/${user.logo}`
        : '/logo.png';
    const designs = getDesignTemplates(companyName, logoUrl, company);
    const activeDesign = designs.find(d => d.id === selectedDesign) || designs[0];

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        let pages = '';
        for (let i = 0; i < copies; i++) {
            pages += activeDesign.render(i + 1);
        }
        printWindow.document.write(`<!DOCTYPE html><html><head><title>${companyName} - Letterhead</title>
            <style>
                @page{size:A4;margin:0;}
                *{margin:0;padding:0;box-sizing:border-box;}
                body{font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
                .letterhead-page{width:210mm;min-height:297mm;margin:0 auto;padding:0;box-sizing:border-box;position:relative;background:white;page-break-after:always;overflow:hidden;}
                .letterhead-page:last-child{page-break-after:auto;}
                @media print{body{margin:0;}.letterhead-page{page-break-inside:avoid;}}
            </style></head><body>${pages}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    };

    const handleDownloadImage = async () => {
        const element = document.getElementById('letterhead-preview');
        if (!element) return;
        try {
            const dataUrl = await htmlToImage.toPng(element, {
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                skipFonts: true,
                fontEmbedCSS: ''
            });
            const link = document.createElement('a');
            link.download = `${companyName.replace(/[^a-zA-Z0-9]/g, '')}-Letterhead.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error('Error generating image', err);
        }
    };

    // ── Preview mini-renderer for design cards ──
    const MiniPreview = ({ design }) => {
        const isActive = selectedDesign === design.id;
        return (
            <button
                onClick={() => setSelectedDesign(design.id)}
                className={`relative group text-left rounded-[28px] overflow-hidden border-2 transition-all duration-300 ${isActive
                    ? 'border-indigo-500 shadow-xl shadow-indigo-100 scale-[1.02]'
                    : 'border-slate-100 hover:border-slate-300 hover:shadow-lg'
                    }`}
                style={{ minWidth: 200 }}
            >
                {/* Selection indicator */}
                {isActive && (
                    <div className="absolute top-3 right-3 z-10 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                        <Check size={14} className="text-white" />
                    </div>
                )}

                {/* Mini letterhead visual */}
                <div className="p-4 bg-white">
                    <div className="rounded-xl overflow-hidden border border-slate-100" style={{ height: 160 }}>
                        {/* Mini header */}
                        <div className="h-1" style={{ background: design.id === 'minimal' ? '#0f172a' : `linear-gradient(90deg, ${design.preview.headerBg}, ${design.preview.accent})` }}></div>
                        <div className="px-3 py-2 flex items-center gap-2" style={{ background: design.id === 'dark' ? '#0f172a' : 'white', borderBottom: `1.5px solid ${design.preview.accent}30` }}>
                            <img src={logoUrl} alt="" className="h-6 w-auto max-w-[40px] object-contain" />
                            <div className="flex-1 min-w-0">
                                <div className="h-1.5 rounded-full w-16" style={{ background: design.id === 'dark' ? '#f8fafc' : '#0f172a', opacity: 0.8 }}></div>
                                <div className="h-1 rounded-full w-10 mt-1" style={{ background: design.id === 'dark' ? '#475569' : '#94a3b8' }}></div>
                            </div>
                            <div className="text-right">
                                <div className="h-1 rounded-full w-8 ml-auto" style={{ background: '#94a3b8' }}></div>
                                <div className="h-1.5 rounded-full w-12 mt-1" style={{ background: design.preview.accent, opacity: 0.5 }}></div>
                            </div>
                        </div>
                        {/* Mini content area with X watermark */}
                        <div className="relative flex-1" style={{ height: 90 }}>
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.06]">
                                <div className="font-black text-[18px] text-slate-900 uppercase" style={{ transform: 'rotate(-25deg)' }}>
                                    {companyName.substring(0, 8)}
                                </div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
                                <div className="font-black text-[14px] text-slate-900 uppercase" style={{ transform: 'rotate(25deg)' }}>
                                    {companyName.substring(0, 8)}
                                </div>
                            </div>
                        </div>
                        {/* Mini footer */}
                        <div className="px-3 py-1.5 flex gap-2" style={{ background: design.id === 'dark' ? '#0f172a' : '#f8fafc', borderTop: '1px solid #e2e8f040' }}>
                            {[1, 2, 3, 4].map(n => (
                                <div key={n} className="flex items-center gap-1">
                                    <div className="w-2.5 h-2.5 rounded-[3px]" style={{ background: `${design.preview.accent}20`, border: `0.5px solid ${design.preview.accent}40` }}></div>
                                    <div className="h-0.5 w-4 rounded-full" style={{ background: design.id === 'dark' ? '#475569' : '#cbd5e1' }}></div>
                                </div>
                            ))}
                        </div>
                        <div className="h-0.5" style={{ background: `linear-gradient(90deg, ${design.preview.headerBg}, ${design.preview.accent})` }}></div>
                    </div>
                </div>

                {/* Design name & desc */}
                <div className={`px-5 pb-4 pt-1 ${isActive ? 'bg-indigo-50/50' : 'bg-white'}`}>
                    <h3 className={`text-sm font-black uppercase tracking-wide ${isActive ? 'text-indigo-600' : 'text-slate-900'}`}>
                        {design.name}
                    </h3>
                    <p className="text-[10px] font-semibold text-slate-400 mt-0.5 leading-tight">{design.desc}</p>
                </div>
            </button>
        );
    };

    // ── Full-size A4 preview renderer ──
    const renderA4Preview = () => {
        const d = activeDesign;
        // We create an inline HTML preview using dangerouslySetInnerHTML
        const previewHtml = d.render(1);
        return (
            <div className="bg-white shadow-2xl border border-slate-200 overflow-hidden" style={{ maxWidth: '100%' }}>
                <div
                    id="letterhead-preview"
                    style={{ width: '210mm', minHeight: '297mm', position: 'relative', background: '#ffffff' }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
            </div>
        );
    };

    return (
        <div className="space-y-8 p-4 min-h-screen">
            {/* Page Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
                        Letterhead Studio
                    </h1>
                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2 italic">
                        {`${companyName} • Choose a design, customize & print`}
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleDownloadImage}
                        className="bg-white text-indigo-600 border-2 border-indigo-600 px-6 py-3.5 rounded-2xl font-black text-[10px] uppercase shadow-sm hover:bg-indigo-50 transition-all flex items-center gap-2"
                    >
                        <Download size={18} /> Download Image
                    </button>
                    <button
                        onClick={handlePrint}
                        className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2"
                    >
                        <Printer size={18} /> Print Letterhead
                    </button>
                </div>
            </div>

            {/* ══ DESIGN SELECTOR ══ */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Choose Design Template</h2>
                    <span className="text-[10px] font-bold text-indigo-500 uppercase">{designs.length} designs</span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-2 scrollbar-hide">
                    {designs.map(d => <MiniPreview key={d.id} design={d} />)}
                </div>
            </div>

            {/* ══ CONTROLS BAR ══ */}
            <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm p-6 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Copies</span>
                    <div className="flex items-center bg-slate-50 rounded-xl border border-slate-100">
                        <button onClick={() => setCopies(Math.max(1, copies - 1))} className="p-2.5 hover:bg-slate-100 rounded-l-xl transition-all text-slate-400"><Minus size={14} /></button>
                        <span className="px-5 py-2 font-black text-indigo-600 text-lg min-w-[50px] text-center">{copies}</span>
                        <button onClick={() => setCopies(Math.min(10, copies + 1))} className="p-2.5 hover:bg-slate-100 rounded-r-xl transition-all text-slate-400"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            {/* ══ A4 LIVE PREVIEW ══ */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden">
                <div className="bg-slate-900 text-white px-8 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <FileText size={18} className="text-indigo-400" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em]">Live Preview — {activeDesign.name}</span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{copies} page{copies > 1 ? 's' : ''} queued</span>
                </div>
                <div className="p-10 bg-slate-50 flex justify-center">
                    {renderA4Preview()}
                </div>
            </div>
        </div>
    );
};

export default Letterhead;
